/**
 * GPS WebSocket Authentication Manager
 * Handles JWT token management and URL generation
 */

/**
 * Authentication configuration
 */
export interface AuthConfig {
  baseUrl: string;
  authMethod: 'query' | 'header' | 'message';
}

/**
 * Authentication Manager class
 * Separated authentication logic for better maintainability
 */
export class AuthManager {
  private config: AuthConfig;
  private currentToken: string | null = null;

  constructor(config: AuthConfig) {
    this.config = config;
  }

  /**
   * Set JWT authentication token
   */
  setToken(token: string): void {
    this.currentToken = token;
  }

  /**
   * Get current JWT token
   */
  getToken(): string | null {
    return this.currentToken;
  }

  /**
   * Check if token is valid
   */
  hasValidToken(): boolean {
    return !!(this.currentToken && this.currentToken.length > 0);
  }

  /**
   * Generate connection URL based on auth method
   */
  generateConnectionUrl(): string {
    if (!this.hasValidToken()) {
      return this.config.baseUrl;
    }

    switch (this.config.authMethod) {
      case 'query':
        return `${this.config.baseUrl}?token=${encodeURIComponent(this.currentToken!)}`;
      case 'header':
      case 'message':
        return this.config.baseUrl;
      default:
        return `${this.config.baseUrl}?token=${encodeURIComponent(this.currentToken!)}`;
    }
  }

  /**
   * Create authentication message for message-based auth
   */
  createAuthMessage(): object | null {
    if (!this.hasValidToken() || this.config.authMethod !== 'message') {
      return null;
    }

    return {
      type: 'auth',
      token: this.currentToken,
      timestamp: Date.now()
    };
  }

  /**
   * Update authentication configuration
   */
  updateConfig(newConfig: Partial<AuthConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Clear authentication data
   */
  clear(): void {
    this.currentToken = null;
  }
}